import { useCallback, useEffect, useState } from 'react';
import { User, Mail, Shield, Search, Plus, Trash2, Edit2, MoreVertical, Users as UsersIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DropdownMenu } from '../../components/ui/DropdownMenu';
import { InlineConfirmation } from '../../components/ui/InlineConfirmation';
import { apiClient } from '../../lib/api-client';
import { CreateUserDialog } from '../../components/admin/CreateUserDialog';
import { EditUserDialog } from '../../components/admin/EditUserDialog';
import { ManageUserGroupsDialog } from '../../components/admin/ManageUserGroupsDialog';
import { usePermission } from '../../hooks/usePermission';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';
import { useCacheEvents } from '../../hooks/useCacheEvents';
import { PageHeader } from '../../components/ui/PageHeader';
import { Skeleton } from '../../components/ui/Skeleton';

// User interface that supports both RBAC YAML and old OCI Identity structures
interface PortalUser {
  // New RBAC YAML structure (primary)
  email?: string;
  name?: string | {
    givenName?: string;
    familyName?: string;
    formatted?: string;
  };
  description?: string;
  
  // Old OCI Identity structure (legacy, for backward compatibility only)
  id?: string;
  userName?: string;
  emails?: Array<{
    value: string;
    primary?: boolean;
  }>;
  groups?: Array<{
    value: string;
    display: string;
  }>;
}

export function Users() {
  const { isAllowed: canView } = usePermission('users', 'read');
  const { isAllowed: canCreateUser } = usePermission('users', 'create_user');
  const { isAllowed: canEditUser } = usePermission('users', 'edit_user');
  const { isAllowed: canManageGroups } = usePermission('users', 'manage_groups');
  const { isAllowed: canDeleteUser } = usePermission('users', 'delete');
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [userGroups, setUserGroups] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isManageGroupsDialogOpen, setIsManageGroupsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<PortalUser | null>(null);
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState<string | null>(null);

  useActivityHeartbeat('cache:rbac', canView);
  
  useEffect(() => {
    void fetchUsers();
  }, []);
  
  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/api/admin/users') as any;
      const fetchedUsers = response.users || [];
      setUsers(fetchedUsers);
      
      // Fetch group memberships for each user
      const groupsMap: Record<string, string[]> = {};
      await Promise.all(
        fetchedUsers.map(async (user: PortalUser) => {
          const userEmail = (user as any).email || user.emails?.[0]?.value || '';
          if (userEmail) {
            try {
              const groupsRes = await apiClient.get(`/api/admin/users/${encodeURIComponent(userEmail)}/groups`) as any;
              groupsMap[userEmail] = groupsRes.groups || [];
            } catch (err) {
              console.error(`Failed to fetch groups for ${userEmail}:`, err);
              groupsMap[userEmail] = [];
            }
          }
        })
      );
      setUserGroups(groupsMap);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refresh only when backend RBAC cache version changes.
  useCacheEvents({
    enabled: canView,
    cacheNames: ['rbac'],
    onEvent: () => {
      void fetchUsers();
    },
  });
  
  const handleCreateUser = async (email: string, name: string, backgroundPreference: 'light' | 'dark') => {
    await apiClient.post('/api/admin/users', {
      email,
      name,
      backgroundPreference
    });
    await fetchUsers();
  };

  const handleEditUser = (user: PortalUser) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  };

  const handleUpdateUser = async (email: string, name: string, backgroundPreference: 'light' | 'dark') => {
    await apiClient.put(`/api/admin/users/${encodeURIComponent(email)}`, {
      name,
      backgroundPreference
    });
    await fetchUsers();
  };
  
  const handleManageGroups = (user: PortalUser) => {
    setSelectedUser(user);
    setIsManageGroupsDialogOpen(true);
  };
  
  const handleDeleteUser = async (user: PortalUser) => {
    const userId = (user as any).email || user.id || '';
    setDeleteConfirmUserId(userId);
  };
    
  const confirmDeleteUser = async (userId: string) => {
    try {
      await apiClient.delete(`/api/admin/users/${encodeURIComponent(userId)}`);
      await fetchUsers();
      setDeleteConfirmUserId(null);
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user: ' + error.message);
      setDeleteConfirmUserId(null);
    }
  };
  
  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    // New RBAC structure: user.email (string), user.name (string)
    // Old OCI structure: user.emails[0].value, user.name.formatted or user.userName
    const email = (user as any).email || user.emails?.[0]?.value || '';
    const name = (user as any).name || (user.name as any)?.formatted || user.userName || '';
    return (
      name.toLowerCase().includes(searchLower) ||
      email.toLowerCase().includes(searchLower)
    );
  });
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton variant="circle" className="w-10 h-10" />
            <div className="space-y-2">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <Skeleton className="h-9 w-32" />
        </div>

        <div className="space-y-3">
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="w-full min-w-0 overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Skeleton variant="circle" className="w-9 h-9" />
                    <div className="flex-1 min-w-0">
                      <Skeleton className="h-5 w-40" />
                      <div className="flex items-center gap-2 mt-2">
                        <Skeleton variant="circle" className="w-4 h-4" />
                        <Skeleton className="h-4 w-48" />
                      </div>
                    </div>
                  </div>
                  <Skeleton className="h-8 w-8" />
                </div>
              </CardHeader>
              <CardContent>
                <div>
                  <Skeleton className="h-4 w-40 mb-3" />
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-6 w-24 rounded-full" />
                    <Skeleton className="h-6 w-20 rounded-full" />
                    <Skeleton className="h-6 w-28 rounded-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-red-600 dark:text-red-400">Failed to load users: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <PageHeader
        icon={UsersIcon}
        iconColor="text-teal-600 dark:text-teal-400"
        iconBgColor="bg-teal-100 dark:bg-teal-900/30"
        description="Manage portal users"
        count={users.length}
        countLabel="users"
        countColor="text-teal-600 dark:text-teal-400"
        actions={
          canCreateUser ? (
            <div className="w-full sm:w-auto sm:h-full flex items-stretch">
              <Button onClick={() => setIsCreateDialogOpen(true)} className="w-full h-full text-sm py-2">
                <Plus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </div>
          ) : undefined
        }
      />
      
      <div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredUsers.map((user) => {
          // Extract values for both RBAC and OCI structures
          const userEmail = (user as any).email || user.emails?.[0]?.value || '';
          const userName = typeof user.name === 'string' ? user.name : (user.name?.formatted || user.userName || '');
          const userId = (user as any).email || user.id || '';
          
          return (
          <Card key={userId}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                    <User className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="truncate">{userName}</CardTitle>
                    {userEmail && (
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {userEmail}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {(() => {
                  const items = [
                    canEditUser
                      ? {
                          label: 'Edit User',
                          icon: <Edit2 className="w-4 h-4" />,
                          onClick: () => handleEditUser(user),
                        }
                      : null,
                    canManageGroups
                      ? {
                          label: 'Manage Groups',
                          icon: <Shield className="w-4 h-4" />,
                          onClick: () => handleManageGroups(user),
                        }
                      : null,
                    canDeleteUser
                      ? {
                          label: 'Delete User',
                          icon: <Trash2 className="w-4 h-4" />,
                          onClick: () => handleDeleteUser(user),
                          className: 'text-red-600 dark:text-red-400',
                        }
                      : null,
                  ].filter(Boolean) as any[];

                  if (items.length === 0) return null;

                  return (
                    <DropdownMenu
                      trigger={
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      }
                      items={items}
                      align="right"
                    />
                  );
                })()}
              </div>
            </CardHeader>
            
            <CardContent>
              {deleteConfirmUserId === userId ? (
                <InlineConfirmation
                  message={`Delete user "${userName}"? This will remove them from all groups and cannot be undone.`}
                  onConfirm={() => confirmDeleteUser(userId)}
                  onCancel={() => setDeleteConfirmUserId(null)}
                  confirmText="Delete"
                  cancelText="Cancel"
                />
              ) : (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Group Memberships
                </h4>
                {(() => {
                  const groups = userGroups[userEmail] || [];
                  return groups.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {groups.map((groupName) => (
                        <span
                          key={groupName}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          {groupName}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No group memberships
                    </p>
                  );
                })()}
              </div>
              )}
            </CardContent>
          </Card>
          );
        })}
        
        {filteredUsers.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <User className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No users found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm ? 'Try adjusting your search' : 'No users available'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Dialogs */}
      <CreateUserDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateUser}
      />

      {selectedUser && (
        <>
          <EditUserDialog
            isOpen={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            onSubmit={handleUpdateUser}
            currentEmail={(selectedUser as any).email || selectedUser.emails?.[0]?.value || ''}
            currentName={typeof selectedUser.name === 'string' ? selectedUser.name : (selectedUser.name?.formatted || selectedUser.userName || '')}
            currentBackgroundPreference={(selectedUser as any).backgroundPreference || 'dark'}
          />
          
          <ManageUserGroupsDialog
            isOpen={isManageGroupsDialogOpen}
            userId={(selectedUser as any).email || selectedUser.id || ''}
            userEmail={(selectedUser as any).email || selectedUser.emails?.[0]?.value || selectedUser.userName || ''}
            onClose={() => setIsManageGroupsDialogOpen(false)}
            onUpdate={fetchUsers}
          />
        </>
      )}
    </div>
  );
}
